import React from 'react';
import { Box, Button, CircularProgress, Grid, Stack } from '@mui/material';
import { useFormik } from 'formik';
import TextInput from '../components/TextInput';
import { FIELD_SIZE, Section, StickyActions } from '../components/Section';
import { educationDetailsInitialValue, EducationDetailsSchemaValidation } from './Schema';
import { useState } from 'react';

import { Cell } from 'react-table';
import { AiOutlineDelete } from 'react-icons/ai';
import {
  useCurrentUserDetailsQuery,
  useDeleteEducationMutation,
  useGetEducationDetailQuery,
  useUpdateEducationDetail
} from 'redux/reducers/user/dashboard/tms-user';
import DataTable from 'components/ui/DataTable/data-table';
import { toast } from 'react-toastify';
import ModalForm from 'components/ui/Modal/UserModal';

function EducationDetails({
  activeStep,
  setActiveStep
}: {
  activeStep: number;
  setActiveStep: (step: number) => void;
}) {
  const { data: CurrentUser, isLoading: isCurrentUserLoading } = useCurrentUserDetailsQuery();
  const TraineeId = CurrentUser?.id;

  const { mutate: updateEducationDetail, isLoading: isUpdating } = useUpdateEducationDetail(
    TraineeId ?? ''
  );

  const [educationId, setEducationId] = useState<string>('');

  const {
    mutate: deleteEducation,
    isLoading: isDeleting,
    isSuccess: isDeleteSuccess
  } = useDeleteEducationMutation();

  const cols = [
    {
      Header: 'Degree',
      accessor: 'degree'
    },
    { Header: 'Institution Name', accessor: 'institution_name' },
    { Header: 'Percentege', accessor: 'percentage' },
    { Header: 'Passed Year', accessor: 'passed_year' },
    { Header: 'Level', accessor: 'level' },
    {
      Header: 'Status',
      Cell: (cell: Cell<any>) => (
        <AiOutlineDelete
          fontSize={22}
          style={{ cursor: 'pointer' }}
          onClick={(_) => {
            setEducationId(cell.row.original.id);
            setModalOpen(!modalOpen);
          }}
        />
      )
    }
  ];

  const [modalOpen, setModalOpen] = useState(false);
  const { data: EducationDetail, isLoading: isEducationDetailLoading } = useGetEducationDetailQuery(
    TraineeId ?? ''
  );

  React.useEffect(() => {
    if (isDeleteSuccess) {
      setModalOpen(!modalOpen);
    }
  }, [isDeleteSuccess]);

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: EducationDetailsSchemaValidation,
    initialValues: educationDetailsInitialValue,
    onSubmit: async (values) => {
      try {
        updateEducationDetail(values);
        await toast.success('Education Detail Successfully Updated');
        formik.resetForm();
      } catch (err) {
        console.log(err);
      }
    }
  });

  const isSaving = isUpdating || formik.isSubmitting;

  if (isCurrentUserLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 320 }}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <>
      <ModalForm
        title={`Are you sure you want to delete this education details?`}
        open={modalOpen}
        modalSize="sm"
        onClose={() => setModalOpen(false)}
      >
        <Stack
          direction={{ xs: 'column-reverse', sm: 'row' }}
          spacing={1.5}
          justifyContent="flex-end"
        >
          <Button variant="outlined" color="inherit" onClick={() => setModalOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress color="inherit" size={16} /> : undefined}
            onClick={() => {
              deleteEducation({
                traineeId: TraineeId ?? '',
                educationId: educationId
              });
            }}
          >
            Delete
          </Button>
        </Stack>
      </ModalForm>

      <form onSubmit={formik.handleSubmit}>
        <Section
          title="Add Education"
          description="Add each qualification you have completed, one entry at a time."
        >
          <TextInput
            {...FIELD_SIZE}
            label="Degree"
            name="degree"
            type="text"
            onChange={formik.handleChange}
            value={formik.values.degree}
            onBlur={formik.handleBlur}
            formik={formik}
            required
          />
          <TextInput
            {...FIELD_SIZE}
            label="Institution Name"
            name="institution_name"
            type="text"
            onChange={formik.handleChange}
            value={formik.values.institution_name}
            onBlur={formik.handleBlur}
            formik={formik}
            required
          />
          <TextInput
            {...FIELD_SIZE}
            label="Percentege"
            name="percentage"
            type="text"
            onChange={formik.handleChange}
            value={formik.values.percentage}
            onBlur={formik.handleBlur}
            formik={formik}
            required
          />
          <TextInput
            {...FIELD_SIZE}
            label="Passed Year"
            name="passed_year"
            type="date"
            onChange={formik.handleChange}
            value={formik.values.passed_year}
            formik={formik}
          />
          <TextInput
            {...FIELD_SIZE}
            label="Level"
            name="level"
            type="text"
            onChange={formik.handleChange}
            value={formik.values.level}
            onBlur={formik.handleBlur}
            formik={formik}
            required
          />

          <Grid item xs={12}>
            <Stack direction="row" justifyContent={{ xs: 'stretch', sm: 'flex-end' }}>
              <Button
                type="submit"
                variant="contained"
                disabled={isSaving}
                startIcon={isSaving ? <CircularProgress color="inherit" size={16} /> : undefined}
                sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 120 }}
              >
                Add
              </Button>
            </Stack>
          </Grid>
        </Section>

        <Section
          title="Education History"
          description="Qualifications you have already saved. Use the delete icon to remove one."
        >
          <Grid item xs={12}>
            <Box sx={{ overflowX: 'auto' }}>
              <DataTable
                data={EducationDetail?.data || []}
                columns={cols}
                isLoading={isEducationDetailLoading}
                hidePagination={true}


              />
            </Box>
          </Grid>
        </Section>

        <StickyActions>
          {activeStep > 1 && (
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => setActiveStep(--activeStep)}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Previous Step
            </Button>
          )}

          <Button
            variant="outlined"
            onClick={() => setActiveStep(++activeStep)}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Next
          </Button>
        </StickyActions>
      </form>
    </>
  );
}

export default EducationDetails;
